// Pure helpers of the Vault pages (detail and list). node --test src/lib/vault.test.ts
import type { HostInfo, HostSample } from './api.ts';

export type Meter = 'cpu' | 'disk' | 'memory';
export type LoadTone = 'ok' | 'warn' | 'fail';

/** A fraction 0..1 as a whole percent: 0.623 → "62%". Unknown stays a dash. */
export function percent(frac: number | null | undefined): string {
  if (frac === null || frac === undefined || !Number.isFinite(frac)) return '—';
  return `${Math.round(Math.min(1, Math.max(0, frac)) * 100)}%`;
}

/** Share of a total, as a fraction clamped to 0..1; null when either is unknown or the total is nothing. */
export function share(part: number | null | undefined, total: number | null | undefined): number | null {
  if (part === null || part === undefined || total === null || total === undefined || !(total > 0)) return null;
  return Math.min(1, Math.max(0, part / total));
}

/** CPU at 90 % or more and a data disk 85 % full deserve a look (warn), a disk 95 % full is failing; memory is shown without a verdict (the OS counts its cache as used). */
export function loadTone(meter: Meter, frac: number | null | undefined): LoadTone {
  if (frac === null || frac === undefined) return 'ok';
  if (meter === 'disk') return frac >= 0.95 ? 'fail' : frac >= 0.85 ? 'warn' : 'ok';
  if (meter === 'cpu') return frac >= 0.9 ? 'warn' : 'ok';
  return 'ok';
}

export interface DiskUse {
  total: number; free: number; used: number; usedFrac: number;
  /** The vault's own data on that disk, as a fraction of the disk; null when the vault does not say. */
  dataFrac: number | null;
}

/** The data disk: used = total − free; the vault's own bytes as a marked part of the used. */
export function diskUse(host: Pick<HostInfo, 'diskTotal' | 'diskFree'> | null | undefined, bytesOnDisk?: number | null): DiskUse | null {
  if (!host || host.diskTotal === null || host.diskFree === null || !(host.diskTotal > 0)) return null;
  const used = Math.max(0, host.diskTotal - host.diskFree);
  const data = bytesOnDisk === null || bytesOnDisk === undefined ? null : Math.min(bytesOnDisk, used);
  return { total: host.diskTotal, free: host.diskFree, used, usedFrac: used / host.diskTotal, dataFrac: data === null ? null : data / host.diskTotal };
}

/** The OS's free memory against the total: small by nature (the cache is not counted), so it is shown as "free", not as a warning. */
export function memoryFree(host: Pick<HostInfo, 'memTotal' | 'memFree'> | null | undefined): { total: number; free: number; freeFrac: number } | null {
  if (!host || host.memTotal === null || host.memFree === null || !(host.memTotal > 0)) return null;
  return { total: host.memTotal, free: host.memFree, freeFrac: Math.min(1, host.memFree / host.memTotal) };
}

type Address = HostInfo['addresses'][number];

/** Addresses for people: IPv4 first and shown; the IPv6 ones (and repeats) go behind "+N more". Without IPv4 the first IPv6 is shown. */
export function orderAddresses(list: Address[] | null | undefined): { shown: Address[]; more: Address[] } {
  const seen = new Set<string>();
  const unique = (list ?? []).filter((a) => a.address && !seen.has(a.address) && !!seen.add(a.address));
  const v4 = unique.filter((a) => a.family === 'ipv4');
  const v6 = unique.filter((a) => a.family !== 'ipv4');
  if (v4.length) return { shown: v4, more: v6 };
  return { shown: v6.slice(0, 1), more: v6.slice(1) };
}

/** How long a vault has been up, in the two largest units: "3 d 4 h", "2 h 15 min", "45 s". */
export function uptimeWords(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !(ms >= 0)) return '—';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return m % 60 ? `${h} h ${m % 60} min` : `${h} h`;
  const d = Math.floor(h / 24);
  return h % 24 ? `${d} d ${h % 24} h` : `${d} d`;
}

/** Uptime now from a status report: the start time when the vault gives it, else its uptime plus the age of the report. */
export function uptimeNow(status: Record<string, unknown> | null | undefined, lastSeen: number | null | undefined, now = Date.now()): number | null {
  if (!status) return null;
  if (typeof status.startedAt === 'number' && status.startedAt <= now) return now - status.startedAt;
  if (typeof status.uptimeMs === 'number') return status.uptimeMs + (lastSeen ? Math.max(0, now - lastSeen) : 0);
  return null;
}

/** The history of one figure for a sparkline; samples without it are left out. */
export function series(history: HostSample[] | undefined, key: 'cpuSystem' | 'load1'): number[] {
  return (history ?? []).map((h) => h[key]).filter((v): v is number => typeof v === 'number' && Number.isFinite(v));
}

/** A line of SVG points (viewBox 0 0 w h) for values scaled to 0..max (default 1, the fraction scale). */
export function sparkPoints(values: number[], w: number, h: number, max = 1): string {
  if (values.length < 2) return '';
  const top = Math.max(max, 1e-9);
  return values.map((v, i) => `${((i / (values.length - 1)) * w).toFixed(1)},${(h - Math.min(1, Math.max(0, v / top)) * h).toFixed(1)}`).join(' ');
}

/** The one-line load for a list row: "disk 62% used" (and "CPU 93%" when it is high); null without host data. */
export function loadLine(host: HostInfo | null | undefined): { text: string; tone: LoadTone } | null {
  const d = diskUse(host);
  if (!d) return null;
  const cpu = host?.cpuSystem ?? null;
  const dt = loadTone('disk', d.usedFrac);
  const ct = loadTone('cpu', cpu);
  const parts = [`disk ${percent(d.usedFrac)} used`];
  if (ct !== 'ok') parts.push(`CPU ${percent(cpu)}`);
  const tone: LoadTone = dt === 'fail' ? 'fail' : dt === 'warn' || ct !== 'ok' ? 'warn' : 'ok';
  return { text: parts.join(' · '), tone };
}

/** A note's author for display: the part of the e-mail before the @. */
export function authorName(user: string | null | undefined): string {
  if (!user) return 'someone';
  const at = user.indexOf('@');
  return at > 0 ? user.slice(0, at) : user;
}

/** A name the user typed for a vault: trimmed, 1..80 characters; empty means "back to the reported name" (null). */
export function parseVaultName(input: string): { name: string | null } | { error: string } {
  const v = input.trim();
  if (!v) return { name: null };
  if (v.length > 80) return { error: 'At most 80 characters.' };
  return { name: v };
}

